import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Badge, Dropdown } from 'react-bootstrap';
import { FiUserPlus, FiMoreVertical, FiMail, FiShield, FiUserX, FiUser } from 'react-icons/fi';
import { IOrg, IMembership } from '../../models/org';
import ConfirmDeleteMember from './ConfirmDeleteMember';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { disableMember, getOrgMembers, updateMemberRole } from '../../api/org';
import MemberInvitations from './MemberInvitations';
import InviteMember from './InviteMembers';
import SpinnerFullSection from '../Other/SpinnerFullSection';
import { useOrgContext } from '../../context/org';

type Props = {
    org: IOrg
}

function Members({org}: Props) {
    const [addMember, setAddMember] = useState(false);
    const [deleteMember, setDeleteMember] = useState<IMembership>();
    const [error, setError] = useState<string | null>(null);
    
    const {membership} = useOrgContext();
    const allowed = ["owner", "admin", "editor"];
    const permission = !allowed.includes(membership?.role || "");

    const {data: memberships} = useQuery({
        queryKey: ['Memberships', org.id],
        queryFn: () => getOrgMembers(org.id!),
        enabled: !!org.id,
    });
    
    const queryClient = useQueryClient();
    const { mutateAsync: disableMemberFn, isLoading} = useMutation(disableMember, {
        onSuccess: () => {
            queryClient.invalidateQueries(['Memberships', org.id]);
            setError(null);
        },
        onError: (error: any) => {
            setError("Unexpected error. Please try again or contact admin.");
        },
    });

    const { mutateAsync: updateMemberRoleFn, isLoading: isUpdating} = useMutation(updateMemberRole, {
        onSuccess: () => {
            setError(null);
        },
        onError: (error: any) => {
            setError("Unexpected error. Please try again or contact admin.");
        },
    });
    
    const handleDisableMember = (member: IMembership) => {
        disableMemberFn({
            orgId: org.id, 
            id: member.id
        });
    };

    const handleUpdate = async (member: IMembership, role: string) => {
        await updateMemberRoleFn({
            orgId: org.id,
            id: member.id,
            role
        });
        
        queryClient.invalidateQueries(['Memberships', org.id]);
    };

    const getRoleVariant = (role: string) => {
        switch (role) {
            case 'owner': return 'danger';
            case 'admin': return 'warning';
            case 'editor': return 'info';
            case 'billing': return 'success';
            default: return 'secondary';
        }
    };

    const getStatusVariant = (status: string) => {
        return status === 'active' ? 'success' : 'secondary';
    };

    return (
        <Card className="bg-dark border-secondary" style={{position:'relative'}}>
            {(isLoading || isUpdating) && <SpinnerFullSection />}
            
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="text-white mb-1">Team Members</h5>
                        <p className="text-secondary mb-0 small">
                            Manage organization members and their permissions
                        </p>
                    </div>
                    <Button 
                        disabled={permission} 
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setAddMember(true)}
                        className="d-flex align-items-center gap-2"
                    >
                        <FiUserPlus size={14} />
                        Add Member
                    </Button>
                </div>

                {/* Members List */}
                <div className="members-list">
                    {memberships?.map((member) => (
                        <div key={member.id} className="member-item p-3 border-bottom border-secondary">
                            <Row className="align-items-center">
                                <Col xs={12} md={5} className="mb-2 mb-md-0">
                                    <div className="d-flex align-items-center">
                                        <div className="member-avatar me-3">
                                            <FiUser size={20} className="text-primary" />
                                        </div>
                                        <div>
                                            <div className="text-white fw-semibold">{member.email}</div>
                                            <small className="text-light">
                                                <FiMail size={12} className="me-1" />
                                                {member.email}
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                                
                                <Col xs={6} md={2} className="mb-2 mb-md-0">
                                    <Badge 
                                        bg={getStatusVariant(member.status)} 
                                        className="status-badge"
                                    >
                                        {member.status}
                                    </Badge>
                                </Col>
                                
                                <Col xs={6} md={3} className="mb-2 mb-md-0">
                                    <Badge 
                                        bg={getRoleVariant(member.role)} 
                                        className="role-badge d-flex align-items-center gap-1"
                                    >
                                        <FiShield size={12} />
                                        {member.role}
                                    </Badge>
                                </Col>
                                
                                <Col xs={12} md={2} className="text-md-right">
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            disabled={permission}
                                            variant="outline-light"
                                            size="sm"
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiMoreVertical size={14} />
                                            Actions
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="bg-dark border-secondary">
                                            <Dropdown.Header className="text-secondary small">
                                                Change Role
                                            </Dropdown.Header>
                                            <Dropdown.Item 
                                                onClick={() => handleUpdate(member, "viewer")}
                                                className="text-white"
                                            >
                                                <FiUser size={12} className="me-2" />
                                                Viewer
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                onClick={() => handleUpdate(member, "editor")}
                                                className="text-white"
                                            >
                                                <FiUser size={12} className="me-2" />
                                                Editor
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                onClick={() => handleUpdate(member, "admin")}
                                                className="text-white"
                                            >
                                                <FiShield size={12} className="me-2" />
                                                Admin
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                onClick={() => handleUpdate(member, "billing")}
                                                className="text-white"
                                            >
                                                <FiUser size={12} className="me-2" />
                                                Billing
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                onClick={() => handleUpdate(member, "owner")}
                                                className="text-white"
                                            >
                                                <FiShield size={12} className="me-2" />
                                                Owner
                                            </Dropdown.Item>
                                            
                                            <Dropdown.Divider className="border-secondary" />
                                            
                                            <Dropdown.Item 
                                                onClick={() => handleDisableMember(member)}
                                                className="text-warning"
                                            >
                                                <FiUserX size={12} className="me-2" />
                                                {member.status === "active" ? "Disable" : "Enable"}
                                            </Dropdown.Item>
                                            
                                            <Dropdown.Item 
                                                onClick={() => setDeleteMember(member)}
                                                className="text-danger"
                                            >
                                                <FiUserX size={12} className="me-2" />
                                                Remove
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </div>

                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3 d-flex align-items-center"
                        style={{ 
                            backgroundColor: 'rgba(220, 53, 69, 0.2)', 
                            borderColor: '#dc3545',
                            color: '#ffffff'
                        }}
                    >
                        <FiUserX className="me-2" size={16} />
                        {error}
                    </Alert>
                )}

                {permission && (
                    <Alert 
                        variant="warning" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: 'rgba(255, 193, 7, 0.2)', 
                            borderColor: '#ffc107',
                            color: '#ffffff'
                        }}
                    >
                        You don't have permission to manage organization members
                    </Alert>
                )}

                {/* Modals */}
                {addMember && (
                    <InviteMember 
                        org={org} 
                        show={addMember} 
                        setShow={setAddMember}
                    />
                )}
                
                {deleteMember && (
                    <ConfirmDeleteMember 
                        orgId={org.id}
                        member={deleteMember} 
                        setError={setError} 
                        cancel={setDeleteMember}
                    />
                )}
            </Card.Body>
        </Card>
    );
}

export default Members;